// src/stores/pipelineStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import service from "@/services/crm/pipeline.service";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";

const pipelineListCache = createScopedCache("pipelines:list");
const PIPELINES_LIST_TTL = 5 * 60 * 1000;

function normalizeStage(s, i = 0) {
  if (typeof s === "string") {
    return { name: s.trim(), sort_order: i };
  }
  return {
    id: s?.id ?? s?.pivot?.id ?? undefined,
    name: String(s?.name ?? "").trim(),
    sort_order: Number.isInteger(s?.sort_order) ? s.sort_order : i,
  };
}
function normalizePipeline(p) {
  const stagesArr = Array.isArray(p?.stages)
    ? p.stages.map((s, i) => normalizeStage(s, i))
    : (p?.stages?.split?.(",") || []).map((n, i) => normalizeStage(n, i));
  return {
    ...p,
    stages: stagesArr,
    status: p?.status ?? "Active",
    is_public: !!p?.is_public,
    user_ids: Array.isArray(p?.user_ids) ? p.user_ids : [],
  };
}

export const usePipelineStore = defineStore("pipeline", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const MAX_PER_PAGE = 200;


  const filters = ref({
    q: "",
    status: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  function indexIntoCache(list = []) {
    const map = Object.create(null);
    list.forEach((raw) => {
      const p = normalizePipeline(raw);
      map[p.id] = p;
    });
    byId.value = map;
  }
  function upsertOne(raw) {
    const p = normalizePipeline(raw);
    byId.value[p.id] = p;
    const idx = items.value.findIndex((x) => x.id === p.id);
    if (idx === -1) items.value.unshift(p);
    else items.value.splice(idx, 1, p);
    return p;
  }

  async function fetchAll(extra = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      params.per_page = Math.min(Number(params.per_page || 25), MAX_PER_PAGE);

      await pipelineListCache.get(params, {
        ttlMs: PIPELINES_LIST_TTL,
        force: extra.force === true,
        loader: async () => {
          const { data } = await service.list(params);
          let list, meta;
          if (data && data.data && data.meta) {
            list = data.data;
            meta = data.meta;
          } else {
            list = data.data ?? data ?? [];
            meta = {
              total: data.total ?? list.length,
              per_page: data.per_page ?? params.per_page ?? 25,
              current_page: data.current_page ?? params.page ?? 1,
            };
          }
          return {
            items: list.map(normalizePipeline),
            meta: {
              total: meta.total,
              per_page: meta.per_page,
              current_page: meta.current_page,
            },
          };
        },
        onHydrate: ({ items: rows, meta }) => {
          items.value = rows || [];
          indexIntoCache(items.value);
          pagination.value = meta || pagination.value;
        },
      });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch pipelines";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureAll(extra = {}) {
    return await fetchAll({ ...extra, force: false });
  }

  // NEW: fetch single pipeline (with stages) for Edit
  async function fetchOne(id, { force = false } = {}) {
    if (!force && byId.value[id] && Array.isArray(byId.value[id].stages)) {
      return byId.value[id];
    }
    loading.value = true;
    try {
      const { data } = await service.get(id); // expects { id, name, stages: [...] }
      return upsertOne(data);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to load pipeline";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      const { data } = await service.create(payload);
      const created = upsertOne(data);
      pipelineListCache.clear();
      toast("Pipeline created");
      // Optionally refresh list if your index has different projections:
      // await fetchAll();
      return created;
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      const { data } = await service.update(id, payload);
      const updated = upsertOne(data);
      pipelineListCache.clear();
      toast("Pipeline updated");
      // Optionally: await fetchAll();
      return updated;
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await service.remove(id);
      // purge from cache
      delete byId.value[id];
      const idx = items.value.findIndex((x) => x.id === id);
      if (idx !== -1) items.value.splice(idx, 1);
      pipelineListCache.clear();
      toast("Pipeline deleted");
      // Optionally: await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    byId,
    pagination,
    loading,
    error,
    filters,
    fetchAll,
    ensureAll,
    fetchOne,   
    create,
    update,
    remove,
  };
});
